import { Box, IconButton, Stack } from '@chakra-ui/react'
import { faFlag, faTrashAlt } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useUser } from '@/client/context/user'
import type { PolicyViolationEntityType } from '@/client/services/moderation'
import { getUserUrl } from '@/client/services/url'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShwLink from '@/components/ShwLink'
import TextWithLines from '@/components/TextWithLines/TextWithLines'
import TutorTag from '@/components/Tutor/TutorTag'
import RelativeTime from '@/components/UI/RelativeTime/RelativeTime'
import Tippy from '@/components/UI/Tippy'
import UserAvatar from '@/components/UserAvatar'

import type { DiscussionCommentProps } from '../types'

const COMMENT_TYPE_TO_ENTITY_TYPE: Record<'session' | 'series', PolicyViolationEntityType> = {
  session: 'session-comment',
  series: 'series-comment',
}

export default function DiscussionCommentCard({
  type,
  comment,
  isTutor,
  highlighted = false,
  onDelete,
}: {
  type: 'session' | 'series'
  comment: DiscussionCommentProps
  isTutor: boolean
  highlighted?: boolean
  onDelete: () => void
}): JSX.Element {
  const currentUser = useUser()
  const [showReportModal, setShowReportModal] = useState(false)

  const { user, message, createdAt, id } = comment

  const allowDelete =
    currentUser.id === user.id ||
    isTutor ||
    hasUserAuthScope(currentUser, 'admin:sessioncomment:delete')

  return (
    <Stack
      direction="column"
      align="flex-start"
      bg={highlighted ? 'yellow.subtle' : 'none'}
      borderBottom={`2px solid ${chakraTokenToVar('border')}`}
      p={6}
      spacing={4}
    >
      <Stack align="center" direction="row" spacing={3}>
        <UserAvatar
          src={user.avatarUrl}
          name={user.displayName}
          size="sm"
          progressionLevel={user.progressionLevel}
        />
        <ShwLink variant="subtle" href={getUserUrl({ userId: user.id, username: user.username })}>
          {user.displayName}
        </ShwLink>
        {user.isTutor ? (
          <Tippy content="This user is a tutor">
            <Box>
              <TutorTag />
            </Box>
          </Tippy>
        ) : null}
      </Stack>
      <Box width="100%">
        <Box>
          <TextWithLines text={message} enableLinks color={comment.isHidden ? 'fg.subtle' : 'fg'} />
        </Box>
        <Stack
          direction="row"
          align={['flex-end', 'center']}
          justify="space-between"
          spacing={2}
          mt={3}
        >
          <Box opacity={0.6} py="4px">
            <RelativeTime time={createdAt} />
          </Box>
          <Stack direction="row" opacity={0.6} spacing="2px">
            <Tippy content="Report Issue">
              <IconButton
                isRound
                onClick={() => setShowReportModal(true)}
                variant="icon"
                aria-label="Report Issue"
                icon={<FontAwesomeIcon icon={faFlag} />}
              />
            </Tippy>
            {allowDelete ? (
              <Tippy content="Delete Comment">
                <IconButton
                  isRound
                  variant="icon"
                  aria-label="Delete comment"
                  onClick={onDelete}
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                />
              </Tippy>
            ) : null}
          </Stack>
        </Stack>
      </Box>
      <ReportModalV2
        entity={{ type: COMMENT_TYPE_TO_ENTITY_TYPE[type], id }}
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserName={user.displayName}
      />
    </Stack>
  )
}
